import {MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Add} from '@mui/icons-material'
import {Button} from '@mui/material'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate, useParams, useSearchParams} from 'react-router'

import {useAnyPermission} from '../../../permissions'
import {CHANGE_MATERIALS_PERMISSION_TYPES} from '../../consts'

import {useMaterialsTreeData} from './hooks/useMaterialsTreeData'
import {useMaterialsWithGlobalName} from './hooks/useMaterialsWithGlobalName'
import {MaterialsTree} from './MaterialsTree'
import {MaterialsTreeFilters, FilterOptions} from './MaterialsTreeFilters'

import {getUrl, PLANT_ROUTES, UrlParams, useUrlParam} from '@settings/modules/routing'

interface MaterialsStructureProps {
  isNewMaterial: boolean
}

export const MaterialsStructure: React.FC<MaterialsStructureProps> = ({isNewMaterial}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const navigate = useNavigate()
  const canChangeMaterials = useAnyPermission(...CHANGE_MATERIALS_PERMISSION_TYPES)

  const {materialType} = useParams<Partial<UrlParams>>()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialQuickFilter = (searchParams.get('filter') as FilterOptions) ?? FilterOptions.All
  const initialFilterText = searchParams.get('filterText') ?? ''

  const [treeQuickFilter, setTreeQuickFilter] = useState<FilterOptions>(initialQuickFilter)
  const [treeFilterText, setTreeFilterText] = useState<string>(initialFilterText)

  const materialsWithGlobalName = useMaterialsWithGlobalName()
  const materialsTreeData = useMaterialsTreeData(materialsWithGlobalName, isNewMaterial)

  const handleSetTreeQuickFilter = useCallback(
    (filter: FilterOptions) => {
      setTreeQuickFilter(filter)

      setSearchParams((params) => {
        const newParams = new URLSearchParams(params)
        if (filter === FilterOptions.All) {
          newParams.delete('filter')
        } else {
          newParams.set('filter', filter)
        }
        return newParams
      })
    },
    [setTreeQuickFilter, setSearchParams]
  )

  const handleSetTreeFilterText = useCallback(
    (text: string) => {
      setTreeFilterText(text)

      setSearchParams((params) => {
        const newParams = new URLSearchParams(params)
        if (text) {
          newParams.set('filterText', text)
        } else {
          newParams.delete('filterText')
        }
        return newParams
      })
    },
    [setTreeFilterText, setSearchParams]
  )

  return (
    <CardBox>
      <MaterialsTreeFilters
        treeQuickFilter={treeQuickFilter}
        filterText={treeFilterText}
        setTreeQuickFilter={handleSetTreeQuickFilter}
        setTreeFilterText={handleSetTreeFilterText}
      />
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<Add />}
        disabled={!canChangeMaterials || isNewMaterial}
        onClick={() =>
          navigate(
            getUrl(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.NEW_MATERIAL.path, {
              plantCode,
              materialType: materialType ?? MaterialType.Cement
            })
          )
        }
        sx={{mt: 3, mb: 3}}
        {...dataTestId('settings-tree-add-new-material-button')}
      >
        {t('materialsSettings.addMaterial')}
      </Button>
      <MaterialsTree
        treeData={materialsTreeData}
        treeQuickFilter={treeQuickFilter}
        treeFilterText={treeFilterText}
      />
    </CardBox>
  )
}
